import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useLocation, useNavigate, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRightLeft } from "lucide-react";
import { useUiTranslation } from "@tubo/ui/i18n";
import { getGetAssetEntityQueryKey, useGetAssetEntity } from "@tubo/api-models/hooks/asset-entity-api.hooks";
import { getGetDataAssetQueryKey, useGetDataAsset } from "@tubo/api-models/hooks/data-asset-api.hooks";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Button } from "@tubo/ui/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@tubo/ui/components/ui/table";
import type { AssetCellEvidence } from "@client/components/semantic-platform/data-assets/asset-extra-hooks";
import { EvidenceList } from "@client/components/semantic-platform/data-assets/views/evidence-list";
import { factValueDisplay, type FactValueDisplayLabels } from "@client/components/semantic-platform/data-assets/fact-value-display";
import { useSecondaryNavTitle } from "@client/components/secondary-nav";
import { EntityEditActions, FactReviseButton } from "@client/components/semantic-platform/data-assets/entities/entity-edit-actions";

const ENTITY_STATUS_KEY: Record<string, "active" | "merged" | "retired"> = {
  active: "active",
  merged: "merged",
  retired: "retired",
};

export const Route = createFileRoute("/_app/semantic-platform/data-assets/entities/$entityId")({
  component: AssetEntityDetailPage,
});

function parseEvidenceRefs(evidenceRefsJson: string | undefined): AssetCellEvidence[] {
  if (!evidenceRefsJson) return [];
  try {
    const parsed: object = JSON.parse(evidenceRefsJson);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is AssetCellEvidence =>
        typeof item === "object" && item !== null && "fileId" in item && "locatorJson" in item,
    );
  } catch {
    return [];
  }
}

/**
 * 实体详情页(波次4 4B-5 最小版):事实(标准值/原始值/修订/证据)+ 关系 + mergedIntoId redirect 展示。
 * 完整版(关系前后导航/工作区对比)仍归 4B-5 卡范围。
 */
function AssetEntityDetailPage() {
  const { t } = useUiTranslation("semanticPlatformDetails");
  const { t: assetT } = useUiTranslation("semanticPlatformAssets");
  const { entityId } = Route.useParams();
  const navigate = useNavigate();
  const { searchStr } = useLocation();
  const listSearch = new URLSearchParams(searchStr).get("ns");
  const { data: entity, isLoading } = useGetAssetEntity(entityId, { query: {} });
  const queryClient = useQueryClient();
  const asset = useGetDataAsset(entity?.assetId ?? entityId, { query: { enabled: !!entity } });

  // 头部注入域级 SecondaryNav,页面体不再自写标题栏
  const headerTitle = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t("assetEntity.backToAsset")}
          disabled={!entity}
          onClick={() => entity && navigate({ to: "/semantic-platform/data-assets/$assetId", params: { assetId: entity.assetId }, search: listSearch ? { ns: listSearch } : {} })}
        >
          <ArrowLeft className="size-4" />
        </Button>
        {entity ? (
          <>
            <span className="text-sm font-semibold">{t("assetEntity.title", { id: entity.id.slice(-8) })}</span>
            <Badge variant={entity.status === "active" ? "default" : "secondary"}>{ENTITY_STATUS_KEY[entity.status] ? t(`assetEntity.status.${ENTITY_STATUS_KEY[entity.status]}`) : entity.status}</Badge>
            <span className="text-sm text-muted-foreground">{entity.entityDefinitionCode}</span>
          </>
        ) : (
          <span className="text-sm font-semibold">{t("assetEntity.moduleName")}</span>
        )}
      </div>
    ),
    [entity, listSearch, navigate, t],
  );
  useSecondaryNavTitle(headerTitle);

  if (isLoading || !entity) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">{t("assetEntity.loading")}</div>;
  }

  const facts = entity.facts ?? [];
  const relations = entity.relations ?? [];
  const factValueLabels: FactValueDisplayLabels = {
    yes: assetT("common.yes"),
    no: assetT("common.no"),
    arraySeparator: assetT("factValue.arraySeparator"),
    entityRef: (idSuffix) => assetT("factValue.entityRef", { idSuffix }),
    nullReason: {
      notMentioned: assetT("factValue.nullReason.notMentioned"),
      undetermined: assetT("factValue.nullReason.undetermined"),
      notApplicable: assetT("factValue.nullReason.notApplicable"),
      sourceMissing: assetT("factValue.nullReason.sourceMissing"),
    },
  };
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: getGetAssetEntityQueryKey(entityId) });
    if (entity.assetId) void queryClient.invalidateQueries({ queryKey: getGetDataAssetQueryKey(entity.assetId) });
  };

  return (
    <div className="flex h-full flex-col">

      <div className="flex-1 overflow-auto px-4 py-3">
        {entity.mergedIntoId && (
          <div className="mb-3 flex items-center gap-2 rounded-md border border-yellow-600/40 bg-yellow-500/5 px-3 py-2 text-sm">
            <ArrowRightLeft className="size-4 text-yellow-700" />
            {t("assetEntity.mergedInto")}
            <Link className="text-primary hover:underline" to="/semantic-platform/data-assets/entities/$entityId" params={{ entityId: entity.mergedIntoId }} search={listSearch ? { ns: listSearch } : {}}>
              {entity.mergedIntoId.slice(-8)}
            </Link>
          </div>
        )}
        {entity.businessLabel && (
          <p className="mb-3 text-sm text-muted-foreground">{entity.businessLabel}</p>
        )}
        {asset.data && (
          <EntityEditActions assetId={entity.assetId} entityId={entity.id} facts={facts.map((f) => ({ propertyCode: f.propertyCode, factRevisionId: f.factRevisionId, value: f.value }))} status={entity.status} />
        )}

        <h2 className="mb-2 text-sm font-medium">{t("assetEntity.facts", { count: facts.length })}</h2>
        <div className="overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("assetEntity.fields.attribute")}</TableHead>
                <TableHead>{t("assetEntity.fields.standardValue")}</TableHead>
                <TableHead>{t("assetEntity.fields.rawValueUnit")}</TableHead>
                <TableHead>{t("assetEntity.fields.revision")}</TableHead>
                <TableHead>{t("assetEntity.fields.changeReason")}</TableHead>
                <TableHead>{t("assetEntity.fields.evidence")}</TableHead>
                <TableHead>{t("assetEntity.fields.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {facts.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">{t("assetEntity.fields.noFacts")}</TableCell></TableRow>
              )}
              {facts.map((f) => (
                <TableRow key={f.factRevisionId}>
                  <TableCell className="text-xs">{f.propertyCode}</TableCell>
                  <TableCell className="max-w-56 truncate font-mono text-xs">{factValueDisplay(f.value, factValueLabels)}</TableCell>
                  <TableCell className="max-w-48 truncate text-xs text-muted-foreground">
                    {f.rawUnit ? `${f.rawUnit}${f.standardUnit && f.standardUnit !== f.rawUnit ? ` → ${f.standardUnit}` : ""}` : "-"}
                  </TableCell>
                  <TableCell className="font-mono text-[10px] text-muted-foreground">#{f.revisionNo}</TableCell>
                  <TableCell className="max-w-40 truncate text-xs">{f.changeReason ?? "-"}</TableCell>
                  <TableCell className="min-w-56">
                    <EvidenceList evidence={parseEvidenceRefs(f.evidenceRefsJson)} />
                  </TableCell>
                  <TableCell className="w-16">
                    {asset.data && entity.status === "active" && (
                      <FactReviseButton
                        assetId={entity.assetId}
                        entityId={entity.id}
                        propertyCode={f.propertyCode}
                        value={f.value}
                        expectedRevision={asset.data.revision}
                        onRevised={refresh}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <h2 className="mb-2 mt-4 text-sm font-medium">{t("assetEntity.relations", { count: relations.length })}</h2>
        <div className="overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("assetEntity.fields.relationType")}</TableHead>
                <TableHead>{t("assetEntity.fields.direction")}</TableHead>
                <TableHead>{t("assetEntity.fields.sourceEntity")}</TableHead>
                <TableHead>{t("assetEntity.fields.targetEntity")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {relations.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">{t("assetEntity.fields.noRelations")}</TableCell></TableRow>
              )}
              {relations.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{r.relationDefinitionCode}</TableCell>
                  <TableCell className="text-xs">{r.direction}</TableCell>
                  <TableCell className="font-mono text-xs">
                    <Link className="text-primary hover:underline" to="/semantic-platform/data-assets/entities/$entityId" params={{ entityId: r.fromEntityId }} search={listSearch ? { ns: listSearch } : {}}>
                      {r.fromEntityId.slice(-8)}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    <Link className="text-primary hover:underline" to="/semantic-platform/data-assets/entities/$entityId" params={{ entityId: r.toEntityId }} search={listSearch ? { ns: listSearch } : {}}>
                      {r.toEntityId.slice(-8)}
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
